import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from './components/MarketingLayout.js';
import MarketingFooterCtas from './components/MarketingFooterCtas.js';
import './Enterprise.css';

export default function Enterprise() {
  return (
    <MarketingLayout>
      <div className="enterprise-page">
        <section className="enterprise-hero">
          <h1>Enterprise & teams</h1>
          <p className="enterprise-subtitle">
            Zolaid is an AI workspace for chat, files, and building—ideal when you want consistent tools across a group.
            Tell us about your team size, security expectations, and billing preferences; we&apos;ll map what fits today and what we can add.
          </p>

          <Link to="/Contact" className="enterprise-cta">
            Contact us
          </Link>
        </section>

        <section className="enterprise-features">
          <h2 className="features-title">What Zolaid actually includes</h2>
          <p className="enterprise-section-lead">
            The product is the same core you see on the public site—no separate &quot;enterprise-only&quot; modules are promised here.
            Enterprise conversations are about rollout, accounts, and requirements—not vapor features.
          </p>

          <div className="features-grid">
            <FeatureCard
              title="AI assistant & workspace"
              description="Chat with context, attachments, and cloud files where configured. Use Zolaid Computer for multi-file sites and code with live preview—aligned with how individuals already use Zolaid, at team scale."
            />
            <FeatureCard
              title="Apps & connections"
              description="Browse the Apps directory and connect services such as GitHub, Slack, Google Drive, Figma, Notion, Jira, and others—coverage varies by integration. See the Integrations page for the current list."
            />
            <FeatureCard
              title="Built-in builders & editors"
              description="Website builder flow, image and video tools (including pro-style editors where your plan allows), and idea-to-asset workflows inside one workspace."
            />
            <FeatureCard
              title="Sign-in options"
              description="Standard accounts support common OAuth providers (e.g. Microsoft, Apple, Facebook) when configured for your deployment. Ask us if you need a specific identity setup—we&apos;ll be honest about what&apos;s supported today."
            />
            <FeatureCard
              title="Team pricing & plans"
              description="See public team pricing where available, or contact us for larger seat counts, invoicing, or questions about plans and limits."
            />
            <FeatureCard
              title="Docs & support"
              description="Product docs cover Builder, Code (mini-IDE), and Terminal setup. For prioritized help or onboarding, reach out—we&apos;ll set expectations on response times when you contact us."
            />
          </div>

          <p className="enterprise-inline-links">
            <Link to="/Integrations" className="contact-link">Integrations</Link>
            {' · '}
            <Link to="/Teams" className="contact-link">Teams</Link>
            {' · '}
            <Link to="/docs" className="contact-link">Docs</Link>
            {' · '}
            <Link to="/pricing" className="contact-link">Pricing</Link>
          </p>
        </section>

        <section className="enterprise-use-cases">
          <h2 className="features-title">Ways teams use Zolaid</h2>
          <p className="enterprise-section-lead">
            These are realistic patterns for an AI assistant plus workspace—not guaranteed outcomes or dedicated product lines.
          </p>
          <div className="use-cases-grid">
            <UseCaseCard
              title="Internal Q&A & drafting"
              description="Draft replies, internal notes, specs, and FAQs from shared context. Useful for support, ops, and leadership—not a replacement for your ticketing system unless you design that workflow."
              icon="💬"
            />
            <UseCaseCard
              title="Marketing & comms drafts"
              description="Iterate on copy, landing ideas, and announcements. Your team keeps brand control; Zolaid speeds iteration."
              icon="✍️"
            />
            <UseCaseCard
              title="Code & site prototypes"
              description="Generate and refine HTML/CSS/JS or starter projects in the workspace with preview—helpful for demos and internal tools, with your usual review and QA."
              icon="💻"
            />
            <UseCaseCard
              title="Docs & long-form help"
              description="Summarize, restructure, and explain documents. Good for onboarding material and handbooks when a human reviews accuracy."
              icon="📄"
            />
            <UseCaseCard
              title="Creative assets"
              description="Use integrated image and video tools for visuals that match your campaigns, subject to plan limits and your approval process."
              icon="🎬"
            />
            <UseCaseCard
              title="Research & exploration"
              description="Explore options, compare approaches, and brainstorm—with the same caution you&apos;d apply to any AI output for decisions that matter."
              icon="🔍"
            />
          </div>
        </section>

        <section className="enterprise-plans">
          <h2 className="features-title">How to buy for an organization</h2>
          <p className="enterprise-section-lead">
            We don&apos;t list fake SLAs or features the app doesn&apos;t ship. Start from public plans, then talk to us if you need something different.
          </p>
          <div className="plans-grid plans-grid--two">
            <PlanCard
              name="Teams (self-serve)"
              price="From public pricing"
              features={[
                'Use team pricing on the Pricing page where offered',
                'Same product features as individual plans at that tier',
                'Email support via Contact',
              ]}
              ctaTo="/pricing"
              ctaLabel="View pricing"
            />
            <PlanCard
              name="Organization (contact us)"
              price="Custom quote"
              features={[
                'Larger seat counts or invoicing—discuss with sales',
                'Security & data questions answered for your review',
                'Deployment and identity needs scoped honestly (no on‑prem or white‑label promised by default)',
              ]}
              popular
            />
          </div>
        </section>

        <section className="enterprise-security">
          <h2 className="features-title">Security & data—straightforward</h2>
          <p className="enterprise-section-lead">
            We don&apos;t claim certifications or infrastructure details on this page unless they are published in your agreement or our official security materials.
            For enterprise due diligence, ask us for the latest answers in writing.
          </p>
          <div className="security-grid">
            <SecurityItem
              title="HTTPS & hosting"
              description="The web app is served over HTTPS. Where your data lives and how long it is retained depend on your deployment and settings—ask us for specifics."
            />
            <SecurityItem
              title="Accounts & access"
              description="Access is tied to your account and the auth methods you enable. Role models and org-wide admin features vary; we can describe what exists today for your setup."
            />
            <SecurityItem
              title="Privacy & terms"
              description="See our Privacy Policy and Terms for the legal baseline. Enterprise customers often need additional review—we expect that and will engage seriously."
            />
            <SecurityItem
              title="AI & sensitive data"
              description="Treat prompts and uploads like any cloud AI tool: avoid secrets you would not send to a third-party model provider. We can discuss data handling options when you contact us."
            />
          </div>
          <p className="enterprise-inline-links">
            <Link to="/PrivacyPolicy" className="contact-link">Privacy Policy</Link>
            {' · '}
            <Link to="/TermsOfService" className="contact-link">Terms of Service</Link>
            {' · '}
            <Link to="/Contact" className="contact-link">Contact</Link>
          </p>
        </section>

        <section className="enterprise-bottom-cta">
          <h2>Talk to us about your org</h2>
          <p>Share team size, use cases, and any security or procurement steps you need. We&apos;ll reply with what Zolaid can do now and what is on the roadmap—not a generic enterprise brochure.</p>
          <Link to="/Contact" className="enterprise-demo-btn">
            Contact us
          </Link>
        </section>

        <MarketingFooterCtas />
      </div>
    </MarketingLayout>
  );
}

function FeatureCard({ title, description }) {
  return (
    <div className="feature-card">
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  );
}

function UseCaseCard({ title, description, icon }) {
  return (
    <div className="use-case-card">
      <div className="use-case-icon">{icon}</div>
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  );
}

function PlanCard({ name, price, features, popular, ctaTo = '/Contact', ctaLabel = 'Contact us' }) {
  return (
    <div className={`plan-card ${popular ? 'popular' : ''}`}>
      {popular && <div className="popular-badge">Most organizations</div>}
      <h3>{name}</h3>
      <div className="plan-price">{price}</div>
      <ul className="plan-features">
        {features.map((feature, index) => (
          <li key={index}>✓ {feature}</li>
        ))}
      </ul>
      <Link to={ctaTo} className="plan-cta-btn">
        {ctaLabel}
      </Link>
    </div>
  );
}

function SecurityItem({ title, description }) {
  return (
    <div className="security-item">
      <h4>{title}</h4>
      <p>{description}</p>
    </div>
  );
}
